import type { PropsWithChildren } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { TypedTranslate, useI18n } from '@change-corgi/core-react/i18n';

type Props = {
	forwardText: TranslationKey | null;
	desktopBackwardText: TranslationKey | null;
	mobileBackwardText: TranslationKey | null;
	forwardFunc: () => void | Promise<void>;
	backwardFunc: () => void | Promise<void>;
	validated: boolean;
	nextStepLoading: boolean;
};

type ActionButtonProps = {
	forwardText: TranslationKey | null;
	backwardText: TranslationKey | null;
	forwardFunc: () => void | Promise<void>;
	backwardFunc: () => void | Promise<void>;
	validated: boolean;
	nextStepLoading: boolean;
};

export const NavButtons: React.FC<Props> = ({
	forwardText,
	desktopBackwardText,
	mobileBackwardText,
	forwardFunc,
	backwardFunc,
	validated,
	nextStepLoading,
}) => {
	return (
		<>
			<MobileStyling>
				<ActionButtons
					forwardText={forwardText}
					backwardText={mobileBackwardText}
					forwardFunc={forwardFunc}
					backwardFunc={backwardFunc}
					validated={validated}
					nextStepLoading={nextStepLoading}
					data-qa="login-or-signup-footer-nav-buttons-mobile"
				/>
			</MobileStyling>
			<DesktopStyling>
				<ActionButtons
					forwardText={forwardText}
					backwardText={desktopBackwardText}
					forwardFunc={forwardFunc}
					backwardFunc={backwardFunc}
					validated={validated}
					nextStepLoading={nextStepLoading}
					data-qa="login-or-signup-footer-nav-buttons-desktop"
				/>
			</DesktopStyling>
		</>
	);
};

const ActionButtons: React.FC<ActionButtonProps> = ({
	forwardText,
	backwardText,
	forwardFunc,
	backwardFunc,
	validated,
	nextStepLoading,
}) => {
	const { typedTranslate } = useI18n();

	if (!forwardText && !backwardText) return null;

	const translatedForwardText = forwardText && typedTranslate(forwardText);
	return (
		<>
			{backwardText ? (
				<Button
					className="mr-4"
					size="large"
					data-testid="back"
					data-qa="back"
					onClick={backwardFunc}
					disabled={nextStepLoading}
				>
					<TypedTranslate value={backwardText} />
				</Button>
			) : (
				<div></div>
			)}
			{translatedForwardText ? (
				<Button
					className="mr-4 md:mr-0"
					variant="primary"
					size="large"
					disabled={!validated || nextStepLoading}
					data-testid="save-and-continue-click"
					data-qa="save-and-continue-click"
					onClick={forwardFunc}
				>
					{translatedForwardText}
				</Button>
			) : (
				<div></div>
			)}
		</>
	);
};

const DesktopStyling: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	return (
		<div className="hidden md:block">
			<div className="mt-6 flex justify-end">{children}</div>
		</div>
	);
};

const MobileStyling: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	return (
		<div
			// using a height here to reserve the space in the main container
			// so that scrolling can make the content under the nav bar appear
			className="block h-24 md:hidden"
		>
			<div className="fixed bottom-0 left-0 z-50 w-full">
				<div className="bg-background-base flex h-24 items-center justify-end border-t border-solid px-4">
					{children}
				</div>
			</div>
		</div>
	);
};
